import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const SectionLabel = ({ children, color = "green" }) => (
  <div
    className={`text-xs font-bold uppercase tracking-[0.25em] ${
      color === "green" ? "text-[#F68712]" : "text-[#0A192F]/60"
    }`}
  >
    {children}
  </div>
);

export const PrimaryButton = ({ to, children, testId, onClick, type }) => {
  const cls =
    "inline-flex items-center gap-2 bg-[#F68712] hover:bg-[#D87410] text-white font-bold tracking-wide px-7 py-4 transition-colors duration-200 group";
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {children}
        <ArrowRight
          size={18}
          strokeWidth={2.25}
          className="transition-transform duration-200 group-hover:translate-x-1"
        />
      </Link>
    );
  }
  return (
    <button type={type || "button"} onClick={onClick} className={cls} data-testid={testId}>
      {children}
      <ArrowRight
        size={18}
        strokeWidth={2.25}
        className="transition-transform duration-200 group-hover:translate-x-1"
      />
    </button>
  );
};

export const SecondaryButton = ({ to, children, testId }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-2 border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-bold tracking-wide px-7 py-4 transition-colors duration-200"
    data-testid={testId}
  >
    {children}
  </Link>
);

export const PageHero = ({ eyebrow, title, intro, testId }) => (
  <section
    className="relative border-b border-[#E2E8F0] bg-white"
    data-testid={testId || "page-hero"}
  >
    <div className="bb-grid-bg absolute inset-0 opacity-60 pointer-events-none" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-10 lg:pt-16 pb-20 lg:pb-28">
      {eyebrow && <SectionLabel>{eyebrow}</SectionLabel>}
      <h1 className="bb-fade-up mt-5 text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F] max-w-4xl">
        {title}
      </h1>
      {intro && (
        <p className="bb-fade-up bb-delay-2 mt-8 text-lg lg:text-xl text-[#475569] leading-relaxed max-w-3xl">
          {intro}
        </p>
      )}
    </div>
  </section>
);

export const BulletList = ({ items, testId }) => (
  <ul className="space-y-3" data-testid={testId}>
    {items.map((item, i) => (
      <li
        key={i}
        className="flex items-start gap-4 text-base lg:text-lg text-[#0A192F]"
        data-testid={`${testId}-item-${i}`}
      >
        <span className="mt-2.5 w-2 h-2 bg-[#F68712] flex-shrink-0" />
        <span className="font-medium leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

export const CTASection = () => (
  <section className="bg-[#0A192F] text-white" data-testid="cta-section">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-8">
        <SectionLabel>Next step</SectionLabel>
        <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
          Ready to get your back office under control?
        </h2>
      </div>
      <div className="lg:col-span-4 lg:flex lg:justify-end">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-[#F68712] hover:bg-[#D87410] text-white font-bold tracking-wide px-8 py-5 transition-colors duration-200 group"
          data-testid="cta-section-book-call"
        >
          Book a Call
          <ArrowRight
            size={18}
            strokeWidth={2.25}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </Link>
      </div>
    </div>
  </section>
);
